import React from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Link from 'components/Link'
import SignUpForm from 'components/SignUpForm'
import { Text } from 'components/Box'
import Form from 'components/Form'
import ApolloErrorComponent from 'components/ApolloError'
import { routes } from 'constants/routes'
import {
  Panel as StyledPanel,
  WordBreak,
  BodyPanel,
  PanelFooter,
  Content,
  InvitedEmail,
} from './shared'
import { Inviter } from './types'
import { ParsedError } from 'graphql/apollo/useMutation'
import { trackEvent } from 'modules/analytics'
import RedirectBadge from 'components/RedirectBadge'
import { Title } from 'components/IndexLayout'
import queryString from 'query-string'
import useUserProfile from 'modules/user/useUserProfile'

export interface SignUpAndAcceptMembershipPanelProps {
  shareShortId: string
  invitedEmail: string
  inviter: Inviter
  inviteToken: string
  documentName: string
  acceptShareMembership(): void
  acceptShareMembershipLoading: boolean
  acceptShareMembershipError?: ParsedError
}

const SignUpAndAcceptMembershipPanel = ({
  invitedEmail,
  inviter,
  inviteToken,
  documentName,
  acceptShareMembership,
  acceptShareMembershipLoading,
  acceptShareMembershipError,
}: SignUpAndAcceptMembershipPanelProps) => {
  const location = useLocation()
  const history = useHistory()
  const query = queryString.parse(location.search)
  const { data: profile } = useUserProfile()
  const userEmail = profile?.me.email

  return (
    <StyledPanel>
      <RedirectBadge
        from={location.pathname}
        documentInvitation={{ inviter, documentName }}
      />

      <SignUpForm
        email={invitedEmail}
        onCompleted={() => {
          if (invitedEmail === userEmail) {
            acceptShareMembership()
            trackEvent('SIGN UP - Sign up form success', {
              willContinueToWorkspaceCreation: false,
              formContext: 'share invite',
            })
          } else {
            history.push(
              routes.SHARE_ACCEPT_INVITE.create({
                query,
              }),
              {
                from: location.pathname,
              }
            )
          }
        }}>
        {formProps => (
          <StyledPanel>
            <BodyPanel>
              <Title>Create a Sketch Account</Title>
              <Content mb={32}>
                <WordBreak>
                  To access the document now, sign up as{' '}
                  <InvitedEmail>
                    <strong>{invitedEmail}</strong>
                  </InvitedEmail>
                  . Alternatively, sign up or{' '}
                  <Link
                    to={{
                      pathname: routes.SHARE_ACCEPT_INVITE.create({}),
                      search: `?token=${inviteToken}`,
                    }}
                    variant="secondary"
                    isUnderlined>
                    sign in
                  </Link>{' '}
                  with a different email and request access.
                </WordBreak>
              </Content>

              <SignUpForm.Fields {...formProps} isInShareInviteContext={true} />
            </BodyPanel>
            <PanelFooter>
              {(acceptShareMembershipError || formProps.apiError) && (
                <ApolloErrorComponent
                  style={{ textAlign: 'left' }}
                  my={4}
                  error={acceptShareMembershipError || formProps.apiError!}
                />
              )}
              {formProps.status && (
                <Form.Error>
                  <Text textAlign="center" mt={0}>
                    {formProps.status}
                  </Text>
                </Form.Error>
              )}
              <SignUpForm.Submit
                isSubmitting={
                  formProps.isSubmitting || acceptShareMembershipLoading
                }
                onClick={() => {
                  const submitCount = formProps.submitCount + 1
                  trackEvent('SIGN UP - Sign up form submitted', {
                    submitCount: submitCount,
                    formContext: 'share invite',
                  })
                }}
              />
            </PanelFooter>
          </StyledPanel>
        )}
      </SignUpForm>
    </StyledPanel>
  )
}

export default SignUpAndAcceptMembershipPanel
